import type { EmailDesign, EmailIdentity, EmailStyle, EmailTokens } from '../queries/brandEmailTheme';

// Client-side copy of the contrast rules in admin-api email-design/theme.ts
// (checkTokens), so the form can flag a colour as it is typed. The server's
// checks stay the authority: they come back with every preview and save.

const HEX = /^#[0-9a-fA-F]{6}$/;

export function isHexColour(value: string): boolean {
  return HEX.test(value);
}

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function luminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG 2.x contrast ratio between two #rrggbb colours (1 to 21). */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = luminance(hexA);
  const b = luminance(hexB);
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

export interface ContrastRule {
  /** The colour the token is read against. */
  against: keyof EmailTokens;
  /** The minimum ratio (WCAG AA): 4.5 for text, 3 for legal small print. */
  min: number;
}

/** Which tokens are text, what they sit on, and the ratio they need. Background tokens have no rule. */
export const CONTRAST_RULES: Partial<Record<keyof EmailTokens, ContrastRule>> = {
  heading: { against: 'card', min: 4.5 },
  strong: { against: 'card', min: 4.5 },
  body: { against: 'card', min: 4.5 },
  label: { against: 'card', min: 4.5 },
  muted: { against: 'card', min: 4.5 },
  faint: { against: 'card', min: 3 },
  accent: { against: 'card', min: 4.5 },
  highlight: { against: 'card', min: 4.5 },
  buttonText: { against: 'button', min: 4.5 },
  onAccent: { against: 'accent', min: 4.5 },
};

export interface ContrastResult {
  ratio: number;
  min: number;
  against: keyof EmailTokens;
  pass: boolean;
}

/** The contrast of one token against what it sits on, or null when it has no rule or a colour is not yet valid. */
export function tokenContrast(tokens: EmailTokens, key: keyof EmailTokens): ContrastResult | null {
  const rule = CONTRAST_RULES[key];
  if (!rule) return null;
  const fg = tokens[key];
  const bg = tokens[rule.against];
  if (!isHexColour(fg) || !isHexColour(bg)) return null;
  const ratio = contrastRatio(fg, bg);
  return { ratio, min: rule.min, against: rule.against, pass: ratio >= rule.min };
}

/** The colours that are not #rrggbb yet: the design can't be previewed or saved until they are. */
export function invalidColourErrors(tokens: EmailTokens): string[] {
  return (Object.keys(tokens) as (keyof EmailTokens)[])
    .filter((key) => !isHexColour(tokens[key] ?? ''))
    .map((key) => `The ${key} colour "${tokens[key] ?? ''}" is not a six-digit hex colour such as #0a0e1c.`);
}

const IDENTITY_KEYS: (keyof EmailIdentity)[] = [
  'wordmark', 'wordmarkAccent', 'tagline', 'logoUrl', 'logoWidth', 'logoHeight', 'footerTaglineLead', 'footerTaglineAccent', 'signOffName',
  'welcomeIntro', 'welcomeFeatures',
];

const STYLE_KEYS: (keyof EmailStyle)[] = ['headingFont', 'bodyFont', 'radius', 'headlineCase'];

/** A stored style with any missing keys taken from the starter (older rows hold `{}`). */
export function resolveStyle(stored: Partial<EmailStyle> | null | undefined, starter: EmailStyle): EmailStyle {
  return { ...starter, ...(stored ?? {}) };
}

/**
 * Whether two designs would send the same emails: blank, null and missing identity
 * fields count as the same, and blank welcome lines are ignored (the server drops them).
 */
export function sameDesign(a: EmailDesign | null, b: EmailDesign | null): boolean {
  if (a === null || b === null) return a === b;
  const tokenKeys = new Set([...Object.keys(a.tokens), ...Object.keys(b.tokens)]) as Set<keyof EmailTokens>;
  for (const key of tokenKeys) {
    if ((a.tokens[key] ?? '').toLowerCase() !== (b.tokens[key] ?? '').toLowerCase()) return false;
  }
  if (STYLE_KEYS.some((key) => a.style?.[key] !== b.style?.[key])) return false;
  const norm = (value: unknown) => {
    if (Array.isArray(value)) return JSON.stringify(value.map((line) => String(line).trim()).filter(Boolean));
    return value === undefined || value === null ? '' : String(value);
  };
  const identityValue = (design: EmailDesign, key: keyof EmailIdentity) =>
    key === 'welcomeFeatures' ? norm(design.identity.welcomeFeatures ?? []) : norm(design.identity[key]);
  return IDENTITY_KEYS.every((key) => identityValue(a, key) === identityValue(b, key));
}
